// product-sticky.js

(() => {
  'use strict';

  // ----------------------------
  // Utils
  // ----------------------------
  const onDomReady = (cb) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
      cb();
    }
  };

  const installOnce = (key, fn) => {
    const installer = window.__MQDF_installOnce;
    if (typeof installer === 'function') {
      const did = installer(key, fn);
      if (!did && window.Shopify?.app?.debug) console.debug(`[${key}] already loaded, skipping re-init`);
      return did;
    }

    const flag = `__${key.replace(/[^a-z0-9_]/gi, '_').toUpperCase()}_LOADED__`;
    if (window[flag]) {
      if (window.Shopify?.app?.debug) console.debug(`[${key}] already loaded, skipping re-init`);
      return false;
    }
    window[flag] = true;
    fn();
    return true;
  };

  // ============================================================
  // Sticky CTA (show/hide)
  // ============================================================
  installOnce('product-sticky', () => {
    onDomReady(() => {
      const productSticky = document.querySelector('[data-product-sticky]');
      if (!productSticky) return;

      const productFormButtons = document.querySelector('.product-form__buttons');
      const productWizard = document.querySelector('product-wizard');
      const sectionFooter = document.querySelector('.section-footer');

      // Si pas de points d'observation, on ne force rien.
      if (!productFormButtons && !sectionFooter) return;

      let formVisible = true;
      let footerVisible = false;

      const isWizardStepAllowed = () => {
        if (!productWizard) return true;
        return productWizard.getAttribute('current-step') === 'product-presentation';
      };

      const update = () => {
        const canShow = !formVisible && !footerVisible && isWizardStepAllowed();
        productSticky.classList.toggle('show', canShow);
      };

      // 1 seul IO, 2 targets
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.target === productFormButtons) formVisible = entry.isIntersecting;
            else if (entry.target === sectionFooter) footerVisible = entry.isIntersecting;
          }
          update();
        },
        { root: null, threshold: 0.01 }
      );

      if (productFormButtons) io.observe(productFormButtons);
      if (sectionFooter) io.observe(sectionFooter);

      // Si product-wizard existe, on suit les changements d'etape (data-current-step)
      let mo = null;
      if (productWizard) {
        mo = new MutationObserver(() => update());
        mo.observe(productWizard, {
          attributes: true,
          attributeFilter: ['data-current-step'],
        });
      }

      update();

      // Cleanup (sans casser: utile si navigation PJAX/sections Shopify)
      window.addEventListener(
        'pagehide',
        () => {
          try {
            io.disconnect();
          } catch (_) {}
          try {
            mo?.disconnect();
          } catch (_) {}
        },
        { once: true }
      );
    });
  });

  // ============================================================
  // Sticky colonne --sticky-y
  // ============================================================
  installOnce('product-sticky-columns', () => {
    const MIN_W = 990;
    const STICKY_HEADER_H = 70;

    const mql = window.matchMedia(`(min-width:${MIN_W}px)`);

    const left = document.querySelector('[data-sticky-col="left"]');
    const right = document.querySelector('[data-sticky-col="right"]');
    if (!left || !right) return;

    const inner = right.querySelector('.sticky-inner');
    if (!inner) return;

    const docTop = (el) => el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset);
    const docBottom = (el) => el.getBoundingClientRect().bottom + (window.scrollY || window.pageYOffset);

    const getCommonContainer = () => {
      const a = left.parentElement;
      const b = right.parentElement;
      if (a && a === b) return a;
      return b || a || right;
    };

    const state = {
      header: 0,
      rightTop: 0,
      containerBottom: 0,
      innerHeight: 0,
      maxTranslate: 0,
      ticking: false,
      translate: 0,
      lastY: window.scrollY || window.pageYOffset,
      lastDir: 0, // -1 up, +1 down, 0 init
    };

    // Header offset uniquement si scroll up ET sticky-header visible en haut
    const headerEl =
      document.querySelector('sticky-header') ||
      document.querySelector('[data-sticky-header]') ||
      document.querySelector('.sticky-header');

    const isHeaderPinnedVisible = () => {
      if (!headerEl) return false;

      const rect = headerEl.getBoundingClientRect();
      // "pinned" en haut: top ~ 0 et une hauteur non nulle
      // Evite d'appliquer l'offset quand le header est hors ecran ou non sticky
      const topIsAtViewportTop = rect.top >= -1 && rect.top <= 1;
      const hasHeight = rect.height > 0 && rect.bottom > 0;

      return topIsAtViewportTop && hasHeight;
    };

    const getHeaderOffset = () => {
      // Applique l'offset uniquement quand on remonte (menu sticky visible)
      if (state.lastDir === -1 && isHeaderPinnedVisible()) return STICKY_HEADER_H;
      return 0;
    };

    const apply = (y, { force = false } = {}) => {
      state.ticking = false;

      if (!mql.matches) {
        right.style.removeProperty('--sticky-y');
        state.translate = 0;
        state.lastY = y;
        return;
      }

      const vh = window.innerHeight;
      const header = state.header;

      const viewportTop = y + header;
      const viewportBottom = y + vh;

      // Hauteur utile entre sticky-top et bas du viewport
      const available = Math.max(0, vh - header);

      // CAS 1: inner tient dans le viewport => sticky TOP uniquement
      if (state.innerHeight <= available) {
        state.translate = viewportTop - state.rightTop;

        if (state.translate > state.maxTranslate) state.translate = state.maxTranslate;
        if (state.translate < 0) state.translate = 0;

        right.style.setProperty('--sticky-y', `${Math.round(state.translate)}px`);
        state.lastY = y;
        return;
      }

      // CAS 2: inner plus grand que le viewport => suivi haut/bas
      const goingDown = y > state.lastY;

      let innerTop = state.rightTop + state.translate;
      let innerBottom = innerTop + state.innerHeight;

      if (force) {
        // resync apres layout shifts
        if (innerBottom < viewportBottom) {
          state.translate += (viewportBottom - innerBottom);
          innerTop = state.rightTop + state.translate;
          innerBottom = innerTop + state.innerHeight;
        }
        if (innerTop > viewportTop) {
          state.translate -= (innerTop - viewportTop);
        }
      } else if (goingDown) {
        if (innerBottom < viewportBottom) {
          state.translate += (viewportBottom - innerBottom);
        }
      } else {
        if (innerTop > viewportTop) {
          state.translate -= (innerTop - viewportTop);
        }
      }

      if (state.translate > state.maxTranslate) state.translate = state.maxTranslate;
      if (state.translate < 0) state.translate = 0;

      right.style.setProperty('--sticky-y', `${Math.round(state.translate)}px`);
      state.lastY = y;
    };

    const compute = () => {
      const y = window.scrollY || window.pageYOffset;

      // maj direction avant getHeaderOffset (pour appliquer le header uniquement en scroll up)
      if (y > state.lastY) state.lastDir = 1;
      else if (y < state.lastY) state.lastDir = -1;

      state.header = getHeaderOffset();

      const common = getCommonContainer();

      state.rightTop = docTop(right);
      state.containerBottom = docBottom(common);

      // plus stable que offsetHeight quand il y a des changements de layout
      state.innerHeight = inner.getBoundingClientRect().height;

      const visibleTop = state.rightTop + state.header;

      state.maxTranslate = Math.max(0, (state.containerBottom - state.innerHeight) - visibleTop);

      if (state.translate > state.maxTranslate) state.translate = state.maxTranslate;
      if (state.translate < 0) state.translate = 0;

      apply(y, { force: true });
    };

    const onScroll = () => {
      if (state.ticking) return;
      state.ticking = true;

      requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset;

        // maj direction pour header offset sur apply
        if (y > state.lastY) state.lastDir = 1;
        else if (y < state.lastY) state.lastDir = -1;

        const nextHeader = getHeaderOffset();
        if (nextHeader !== state.header) {
          // si l'offset header change (apparition/disparition du sticky-header), on recompute propre
          compute();
          return;
        }

        apply(y);
      });
    };

    const onResize = () => compute();

    const ro = new ResizeObserver(() => compute());
    const common = getCommonContainer();

    ro.observe(left);
    ro.observe(right);
    ro.observe(inner);
    if (common) ro.observe(common);
    if (headerEl) ro.observe(headerEl);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    if (mql.addEventListener) mql.addEventListener('change', compute);
    else if (mql.addListener) mql.addListener(compute);

    compute();

    window.addEventListener(
      'pagehide',
      () => {
        try {
          ro.disconnect();
        } catch (_) {}
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (mql.removeEventListener) mql.removeEventListener('change', compute);
        else if (mql.removeListener) mql.removeListener(compute);
      },
      { once: true }
    );
  });
})();